import React, { useCallback, useMemo } from 'react';
import { Anchor, Box, FieldWrapper, FiservCardStatusEnum, FormFieldsContainer, Inscription, Label } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE, Roles } from '@/common/constants';
import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core/hooks';
import { ReplacementInitiationTypeTitle } from '@/modules/transaction/Cards/Cards.types';
import theme from '@/styles/theme';
import { getCardNumberLabel,getDisplayCardNumber } from '@/utils/modifiers/getDisplayCardNumber';

import CardStatus from './components/CardStatus';
import UserAccessStatusCard from './components/UserAccessStatusCard';
import UserStatus from './components/UserStatus';
import useUserVerificationStatusStore from './stores/useUserVerificationStatus.store';
import { UserVerificationStatusField } from './userVerificationStatus.types';

interface Props {
  hideUserAccess?: boolean;
  setIsProvideFullAccessModalVisible: (value: boolean) => void;
}

const UserVerificationStatus: React.FC<Props> = ({ hideUserAccess, setIsProvideFullAccessModalVisible }) => {
  const roleType = useUserVerificationStatusStore((state) => state?.state?.roleType);
  const isWelcomeEmailSent = useUserVerificationStatusStore((state) => state?.state?.isWelcomeEmailSent);
  const cardLast2 = useUserVerificationStatusStore((state) => state?.state?.cardLast2);
  const cardLast4 = useUserVerificationStatusStore((state) => state?.state?.cardLast4);
  const cardStatus = useUserVerificationStatusStore((state) => state?.state?.cardStatus);
  const cardMailedOn = useUserVerificationStatusStore((state) => state?.state?.cardMailedOn);
  const replacementInitiation = useUserVerificationStatusStore((state) => state?.state?.replacementInitiation);

  const showReplacementInitiation = useMemo(() => cardStatus === FiservCardStatusEnum.PENDING_REPLACE
    || cardStatus === FiservCardStatusEnum.LOST
    || cardStatus === FiservCardStatusEnum.STOLEN
    || cardStatus === FiservCardStatusEnum.NOT_SENT, [cardStatus]);

  const replacementInitiationTitle = useMemo(() => {
    if (!replacementInitiation) {
      return;
    }

    return ReplacementInitiationTypeTitle?.[replacementInitiation];
  }, [replacementInitiation]);

  const hasAccessToLockEmployee = useHasAccess([{ permission: Permissions.EE_FULL_ACCESS }]);
  const hasFullCardNumberPermission = useHasAccess([{ permission: Permissions.VIEW_LAST_4 }]);
  const isEmployeeLimitedAccess = useMemo(() => roleType === Roles.limitedAccess, [roleType]);

  const cardNumberData = useMemo(() => ({ last4: cardLast4, last2: cardLast2 }), [cardLast4, cardLast2]);
  const displayCardNumber = useMemo(() => 
    getDisplayCardNumber(cardNumberData, hasFullCardNumberPermission), 
    [cardNumberData, hasFullCardNumberPermission]
  );
  const cardNumberLabel = useMemo(() => 
    getCardNumberLabel(cardNumberData, hasFullCardNumberPermission), 
    [cardNumberData, hasFullCardNumberPermission]
  );

  const handleOpenProvideFullAccessModal = useCallback(() => {
    setIsProvideFullAccessModalVisible(true);
  }, [setIsProvideFullAccessModalVisible]);

  return (
    <Box
      data-testId="user-verification-status-form"
      background="canvas"
      round="container1Round"
      pad={{ vertical: 'spacing24', horizontal: 'spacing32' }}
    >
      <Inscription data-testId="user-verification-status-form_title" size="20px">
        User Verification Status
      </Inscription>

      <FormFieldsContainer fieldsTopMargin={theme.spacings.spacing4} margin={{ top: 'spacing16' }}>
        <FieldWrapper data-testId={`user-verification-status-form_${UserVerificationStatusField.userStatus}_field`}>
          <Label name={UserVerificationStatusField.userStatus}>
            User status
          </Label>
          <UserStatus role={roleType} isWelcomeEmailSent={isWelcomeEmailSent} />
        </FieldWrapper>

        {!hideUserAccess && (
          <FieldWrapper data-testId={`user-verification-status-form_${UserVerificationStatusField.userAccess}_field`}>
            <Label name={UserVerificationStatusField.userAccess}>
              User access
            </Label>
            <UserAccessStatusCard role={roleType} />
            {hasAccessToLockEmployee && isEmployeeLimitedAccess && (
              <Anchor
                margin={{ left: 'spacing8' }}
                size="medium"
                weight={400}
                onClick={handleOpenProvideFullAccessModal}
              >
                Provide Full Access
              </Anchor>
            )}
          </FieldWrapper>
        )}

        <FieldWrapper data-testId={`user-verification-status-form_${UserVerificationStatusField.primaryCardInfo}_field`}>
          <Label name={UserVerificationStatusField.primaryCardInfo}>
            {cardNumberLabel}
          </Label>
          <Inscription>
            {(cardLast4 || cardLast2) ? displayCardNumber : EMPTY_FIELD_VALUE}
          </Inscription>
        </FieldWrapper>

        <FieldWrapper data-testId={`user-verification-status-form_${UserVerificationStatusField.cardMailedDate}_field`}>
          <Label name={UserVerificationStatusField.cardMailedDate}>
            Card mailed date
          </Label>
          <Inscription>
            {cardMailedOn ? dayjs(cardMailedOn).format(DEFAULT_DATE_FORMAT) : 'Not sent'}
          </Inscription>
        </FieldWrapper>

        <FieldWrapper data-testId={`user-verification-status-form_${UserVerificationStatusField.cardStatus}_field`}>
          <Label name={UserVerificationStatusField.cardStatus}>
            Card status
          </Label>
          <CardStatus />
        </FieldWrapper>

        {replacementInitiation && showReplacementInitiation && (
          <FieldWrapper data-testId={`user-verification-status-form_${UserVerificationStatusField.replacementInitiation}_field`}>
            <Label name={UserVerificationStatusField.replacementInitiation}>
              Replacement initiation
            </Label>
            <Inscription>
              {replacementInitiationTitle}
            </Inscription>
          </FieldWrapper>
        )}
      </FormFieldsContainer>
    </Box>
  );
};

export default UserVerificationStatus;
